/**
 * Sentry wiring for client-side error monitoring, light performance tracing, and
 * auth-event telemetry — under a strict GDPR data-minimisation regime.
 *
 * Auth events arrive through the existing `window.__authTelemetrySink` seam in
 * `logger.ts` (call sites untouched). Before anything leaves the browser this
 * module: allowlists safe fields, scrubs free-text/URLs of personal data, and
 * pseudonymises the user identifier with a one-way SHA-256 hash.
 */
import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/** Fields safe to forward verbatim. Everything else (incl. the open extra-data
 *  bag in AuthEventDetail) is dropped — minimisation by construction. */
const ALLOWLIST = [
	'event',
	'level',
	'errorCode',
	'status',
	'scope',
	'reason',
	'phase',
	'timestamp',
] as const;

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const BEARER_RE = /Bearer\s+[\w.-]+/gi;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Strip emails / UPNs and bearer tokens from free text. */
const scrubMessage = (value: unknown): string | undefined => {
	if (typeof value !== 'string' || !value) {
		return undefined;
	}

	return value.replace(EMAIL_RE, '[email]').replace(BEARER_RE, 'Bearer [redacted]');
};

/** Drop the query string and replace UUID path segments with `:id`. */
const scrubUrl = (value: unknown): string | undefined => {
	if (typeof value !== 'string' || !value) {
		return undefined;
	}

	const [path] = value.split('?');
	return path.replace(UUID_RE, ':id');
};

/** One-way SHA-256 (hex) via Web Crypto, for pseudonymising the user identifier. */
const sha256 = async (input: string): Promise<string> => {
	const data = new TextEncoder().encode(input);
	const digest = await crypto.subtle.digest('SHA-256', data);
	return Array.from(new Uint8Array(digest))
		.map((b) => {
			return b.toString(16).padStart(2, '0');
		})
		.join('');
};

type AuthRecord =
	NonNullable<typeof window.__authTelemetrySink> extends (record: infer R) => void ? R : never;

/** Build the allowlisted, scrubbed data bag shared by both breadcrumb and event paths. */
const safeData = (record: AuthRecord): Record<string, unknown> => {
	const out: Record<string, unknown> = {};
	for (const key of ALLOWLIST) {
		const value = (record as Record<string, unknown>)[key];
		if (value !== undefined) {
			out[key] = value;
		}
	}

	const message = scrubMessage(record.message);
	const url = scrubUrl(record.url);
	if (message) {
		out.message = message;
	}

	if (url) {
		out.url = url;
	}

	return out;
};

const SENTRY_LEVEL: Record<string, Sentry.SeverityLevel> = {
	info: 'info',
	warn: 'warning',
	error: 'error',
};

/**
 * The `__authTelemetrySink` adapter. `info` events become breadcrumbs (context
 * only); `warn`/`error` become captured Sentry events. The user identifier is
 * pseudonymised asynchronously, so capture is deferred (fire-and-forget); the
 * synchronous console + ring-buffer in logger.ts remain the always-on record.
 */
const authTelemetrySink = (record: AuthRecord): void => {
	const data = safeData(record);
	const level = SENTRY_LEVEL[record.level] ?? 'info';

	if (record.level === 'info') {
		Sentry.addBreadcrumb({ category: 'auth', level, message: record.event, data });
		return;
	}

	void (async () => {
		const tags: Record<string, string> = { category: 'auth', auth_event: record.event };
		const rawId = record.correlationId;
		if (typeof rawId === 'string' && rawId) {
			tags.user_hash = await sha256(rawId);
		}

		const scrubbed = scrubMessage(record.message);
		const text = scrubbed ? `[auth:${record.event}] ${scrubbed}` : `[auth:${record.event}]`;

		Sentry.captureMessage(text, {
			level,
			tags,
			fingerprint: ['auth', record.event],
			extra: data,
		});
	})();
};

/**
 * Initialise Sentry. Only active in production builds with a DSN present, so
 * local dev never reports and a missing DSN is a no-op (never a crash).
 */
export const initSentry = (app: App, router: Router): void => {
	const enabled = import.meta.env.PROD && !!DSN;
	if (!enabled) {
		return;
	}

	Sentry.init({
		app,
		dsn: DSN,
		enabled,
		environment:
			(import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
		sendDefaultPii: false,
		integrations: [Sentry.browserTracingIntegration({ router })],
		tracesSampleRate: 0.1,
		// Distributed-trace headers on requests to the APIM origin risk a CORS
		// preflight rejection; keep empty until APIM is confirmed to allow
		// `sentry-trace` / `baggage`. Spans still work without them.
		tracePropagationTargets: [],
		beforeSend(sentryEvent) {
			if (sentryEvent.user) {
				delete sentryEvent.user.ip_address;
			}

			if (sentryEvent.request) {
				const headers = sentryEvent.request.headers;
				if (headers) {
					delete headers.Authorization;
					delete headers.authorization;
				}

				// Drop request/response bodies that could carry personal data.
				delete sentryEvent.request.data;
				sentryEvent.request.url =
					scrubUrl(sentryEvent.request.url) ?? sentryEvent.request.url;
			}

			return sentryEvent;
		},
		beforeBreadcrumb(breadcrumb) {
			if (breadcrumb.data && typeof breadcrumb.data.url === 'string') {
				breadcrumb.data.url = scrubUrl(breadcrumb.data.url) ?? breadcrumb.data.url;
			}

			return breadcrumb;
		},
	});

	// Route auth telemetry through Sentry via the existing seam (logger.ts and
	// its call sites are untouched).
	window.__authTelemetrySink = authTelemetrySink;
};
